import axios from 'axios'
import Cookies from 'js-cookie'
import { apiOperationUrl, tenant } from '@/constants/config'

export const ACCESS_TOKEN_COOKIE = 'current_user_token'
export const REFRESH_TOKEN_COOKIE = 'current_user_refresh_token'

const COOKIE_OPTS = { path: '/' }

let refreshPromise = null
let logoutStarted = false

export function getAccessToken() {
  return Cookies.get(ACCESS_TOKEN_COOKIE)
}

export function getRefreshToken() {
  return Cookies.get(REFRESH_TOKEN_COOKIE)
}

export function setAuthTokens({ token, refresh_token: refreshToken }) {
  logoutStarted = false

  if (token) {
    Cookies.set(ACCESS_TOKEN_COOKIE, token, COOKIE_OPTS)
    axios.defaults.headers.common.Authorization = `Bearer ${token}`
  }

  if (refreshToken) {
    Cookies.set(REFRESH_TOKEN_COOKIE, refreshToken, COOKIE_OPTS)
  } else if (refreshToken === null || refreshToken === '') {
    Cookies.remove(REFRESH_TOKEN_COOKIE, COOKIE_OPTS)
  }
}

export function clearAuthTokens() {
  Cookies.remove(ACCESS_TOKEN_COOKIE, COOKIE_OPTS)
  Cookies.remove(REFRESH_TOKEN_COOKIE, COOKIE_OPTS)
  try {
    delete axios.defaults.headers.common.Authorization
  } catch (e) {
    // ignore
  }
}

function forceLogout() {
  if (logoutStarted) {
    return
  }
  logoutStarted = true

  clearAuthTokens()
  localStorage.removeItem('current_user')
  localStorage.removeItem('current_permissions')
  localStorage.removeItem('current_user_permission')

  if (window.location.pathname !== '/login') {
    window.location.replace('/login')
  }
}

function isAuthRefreshUrl(url) {
  if (!url) return false
  return url.indexOf('/merchant/refresh') !== -1 || url.indexOf('/merchant/login') !== -1
}

function setAuthorizationHeader(headers, token) {
  if (!headers) {
    return { Authorization: `Bearer ${token}` }
  }

  const value = `Bearer ${token}`
  if (typeof headers.set === 'function') {
    headers.set('Authorization', value)
    return headers
  }

  // eslint-disable-next-line no-param-reassign
  headers.Authorization = value
  return headers
}

function getAuthorizationHeader(headers) {
  if (!headers) return null
  if (typeof headers.get === 'function') {
    return headers.get('Authorization') || headers.get('authorization')
  }
  return headers.Authorization || headers.authorization || null
}

/**
 * Access expired + refresh still valid → new access (+ rotated refresh).
 * Refresh expired/invalid → throw → caller forceLogout.
 *
 * Uses fetch (not axios) so Authorization defaults never leak onto /merchant/refresh.
 */
async function refreshAccessToken() {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    throw new Error('NO_REFRESH_TOKEN')
  }

  const base = String(apiOperationUrl || '').replace(/\/$/, '')
  const response = await fetch(`${base}/merchant/refresh`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-tenant': tenant,
    },
    body: JSON.stringify({ refresh_token: refreshToken }),
  })

  let data = {}
  try {
    data = await response.json()
  } catch (e) {
    data = {}
  }

  if (!response.ok) {
    const error = new Error((data && data.code) || (data && data.message) || 'REFRESH_FAILED')
    error.response = { status: response.status, data }
    throw error
  }

  if (!data.token) {
    throw new Error('REFRESH_NO_TOKEN')
  }

  setAuthTokens({
    token: data.token,
    refresh_token: data.refresh_token || null,
  })

  return data.token
}

function shouldAttemptSilentRefresh(error, originalRequest) {
  const status = error.response && error.response.status
  const code = error.response && error.response.data && error.response.data.code
  const url = originalRequest && originalRequest.url

  if (status !== 401 || !originalRequest || originalRequest.authRetry) {
    return false
  }

  if (isAuthRefreshUrl(url)) {
    return false
  }

  if (code === 'TOKEN_REVOKED') {
    return false
  }

  if (!getRefreshToken()) {
    return false
  }

  return code === 'TOKEN_EXPIRED' || !code
}

function shouldForceLogoutWithoutRefresh(error, originalRequest) {
  const status = error.response && error.response.status
  const code = error.response && error.response.data && error.response.data.code
  const url = originalRequest && originalRequest.url

  if (status !== 401 || isAuthRefreshUrl(url)) {
    return false
  }

  if (!getAccessToken()) {
    return false
  }

  if (getRefreshToken() && code !== 'TOKEN_REVOKED') {
    return false
  }

  return code === 'TOKEN_EXPIRED' || code === 'TOKEN_REVOKED' || !code
}

export function attachAuthRequestInterceptor(axiosIns) {
  axiosIns.interceptors.request.use(
    reqConfig => {
      if (!reqConfig.headers) {
        // eslint-disable-next-line no-param-reassign
        reqConfig.headers = {}
      }

      if (reqConfig.authRetry && getAuthorizationHeader(reqConfig.headers)) {
        return reqConfig
      }

      const cacheUserToken = Cookies.get(ACCESS_TOKEN_COOKIE)
      if (cacheUserToken) {
        // eslint-disable-next-line no-param-reassign
        reqConfig.headers = setAuthorizationHeader(reqConfig.headers, cacheUserToken)
      }
      return reqConfig
    },
    error => Promise.reject(error),
  )
}

export function attachAuthResponseInterceptor(axiosIns) {
  axiosIns.interceptors.response.use(
    response => response,
    async error => {
      const originalRequest = error.config
      const status = error.response && error.response.status
      const code = error.response && error.response.data && error.response.data.code

      if (status === 401 && code === 'TOKEN_REVOKED') {
        forceLogout()
        return Promise.reject(error)
      }

      if (shouldAttemptSilentRefresh(error, originalRequest)) {
        // eslint-disable-next-line no-param-reassign
        originalRequest.authRetry = true

        try {
          if (!refreshPromise) {
            refreshPromise = refreshAccessToken().finally(() => {
              refreshPromise = null
            })
          }

          const newToken = await refreshPromise

          // eslint-disable-next-line no-param-reassign
          originalRequest.headers = setAuthorizationHeader(originalRequest.headers, newToken)

          return axiosIns(originalRequest)
        } catch (refreshError) {
          forceLogout()
          return Promise.reject(refreshError)
        }
      }

      if (shouldForceLogoutWithoutRefresh(error, originalRequest)) {
        forceLogout()
        return Promise.reject(error)
      }

      return Promise.reject(error)
    },
  )
}
